import React from "react";
import { formatRecommendationScoreOutOfTen, toRecommendationScoreOutOfTen } from "../../lib/localRecommendationStrength";
import { formatLocalIntentLabel } from "../../lib/localIntentLabels";
import { getLocalVisibilityLabel } from "../../lib/localVisibilityLabel";
import { formatPercentage } from "../../lib/percentageFormatting";

type LocalAIAnalystProps = {
  organicVisibility?: number | null;
  recommendationStrengthOutOfTen?: number | null;
  strongestIntent?: string | null;
  strongestIntentScoreOutOfTen?: number | null;
  weakestIntent?: string | null;
  historicalTrend?: { state: string; message: string } | null;
  runFreshnessLabel?: string;
  recommendedNextMove?: string | null;
};

function getRecommendationLabel(scoreOutOfTen?: number | null) {
  if (scoreOutOfTen == null) return "unavailable";
  if (scoreOutOfTen >= 7) return "high";
  if (scoreOutOfTen >= 5) return "moderate";
  return "low";
}

export default function LocalAIAnalyst({
  organicVisibility,
  recommendationStrengthOutOfTen,
  strongestIntent,
  strongestIntentScoreOutOfTen,
  weakestIntent,
  historicalTrend,
  runFreshnessLabel,
  recommendedNextMove
}: LocalAIAnalystProps) {
  const recommendationScore = toRecommendationScoreOutOfTen(
    recommendationStrengthOutOfTen,
    "score_out_of_ten"
  );
  const visibilityLabel = getLocalVisibilityLabel(organicVisibility).toLowerCase();
  const recommendationLabel = getRecommendationLabel(recommendationScore);

  const strongestIntentLabel = strongestIntent ? formatLocalIntentLabel(strongestIntent) : null;
  const weakestIntentLabel = weakestIntent ? formatLocalIntentLabel(weakestIntent) : null;
  const formattedVisibility = formatPercentage(organicVisibility);
  const recommendationScoreLabel = formatRecommendationScoreOutOfTen(
    recommendationScore,
    "score_out_of_ten"
  );
  const parsedStrongestIntentScore = toRecommendationScoreOutOfTen(
    strongestIntentScoreOutOfTen,
    "score_out_of_ten"
  );
  const strongestIntentNote = strongestIntentLabel && parsedStrongestIntentScore !== null
    ? ` The highest measured intent group was ${strongestIntentLabel} (${formatRecommendationScoreOutOfTen(parsedStrongestIntentScore, "score_out_of_ten")}); this is a relative ranking, and its absolute score is ${getRecommendationLabel(parsedStrongestIntentScore)}.`
    : "";

  let summary = "No usable current-run visibility or recommendation score is available, so no performance interpretation can be made.";
  if (organicVisibility != null && recommendationScore !== null) {
    if (organicVisibility! >= 50 && recommendationScore < 5) {
      summary = `Your business appeared in ${formattedVisibility} of eligible Local responses, but its average recommendation score is low at ${recommendationScoreLabel}. Presence and recommendation strength measure different things.${strongestIntentNote}`;
    } else if (organicVisibility! >= 50 && recommendationScore >= 7) {
      summary = `Your business appeared in ${formattedVisibility} of eligible Local responses, with a high average recommendation score of ${recommendationScoreLabel}.${strongestIntentNote}`;
    } else if (organicVisibility! < 50 && recommendationScore >= 7) {
      summary = `Your business had ${formattedVisibility} organic visibility, while its average recommendation score when measured was high at ${recommendationScoreLabel}.${strongestIntentNote}`;
    } else if (organicVisibility === 0 && recommendationScore === 0) {
      summary = `This run recorded no organic visibility and an average recommendation score of ${recommendationScoreLabel}.${strongestIntentNote}`;
    } else {
      summary = `This run recorded ${formattedVisibility} organic visibility and an average recommendation score of ${recommendationScoreLabel} (${recommendationLabel}).${strongestIntentNote}`;
    }
  } else if (organicVisibility != null) {
    summary = `This run recorded ${formattedVisibility} organic visibility; recommendation strength is unavailable.${strongestIntentNote}`;
  } else if (recommendationScore !== null) {
    summary = `The average recommendation score was ${recommendationScoreLabel}; organic visibility is unavailable.${strongestIntentNote}`;
  }

  const nextMove =
    recommendedNextMove ||
    (weakestIntentLabel && recommendationScore !== null
      ? `Review the completed prompts in ${weakestIntentLabel}, which had the lowest available average recommendation score. The score describes model output, not customer conversion.`
      : organicVisibility != null || recommendationScore !== null
        ? "Review the measured prompts and supporting evidence before choosing a content change. These metrics do not measure customer conversion."
        : "No evidence-backed next step is available until a completed Local measurement can be read.");

  return (
    <section style={styles.card}>
      <div style={styles.header}>
        <div>
          <p style={styles.eyebrow}>AI Analyst</p>
          <h2 style={styles.title}>Local visibility brief</h2>
          <p data-testid="local-ai-analyst-source" style={{ margin: "5px 0 0", color: "#64748b", fontSize: 12 }}>
            Summary based on the latest completed Local run
          </p>
        </div>
      </div>

      <p style={styles.summary}>{summary}</p>
      {runFreshnessLabel?.includes("Older analysis") && (
        <p role="status" data-testid="local-analyst-freshness" style={styles.freshnessNotice}>
          This is an older completed analysis; current conditions may have changed since it was measured.
        </p>
      )}

      <div style={styles.insightGrid}>
        <div style={styles.insightCard}>
          <p style={styles.insightLabel}>Visibility signal</p>
          <p style={styles.insightText}>
            {organicVisibility == null
              ? "Organic visibility is unavailable for this run."
              : `${formattedVisibility} organic visibility, indicating a ${visibilityLabel} local AI presence.`}
          </p>
        </div>

        <div style={styles.insightCard}>
          <p style={styles.insightLabel}>Recommendation signal</p>
          <p style={styles.insightText}>
            {recommendationScore == null
              ? "Recommendation score is unavailable for this run."
              : `${recommendationScoreLabel} average recommendation score (${recommendationLabel}).`}
          </p>
        </div>

        <div style={styles.insightCard}>
          <p style={styles.insightLabel}>Trend context</p>
          <p style={styles.insightText}>
            {historicalTrend?.message || "Validating historical results before reporting a trend."}
          </p>
        </div>
      </div>

      <div style={styles.nextMove}>
        <p style={styles.nextMoveLabel}>Recommended next move</p>
        <p style={styles.nextMoveText}>{nextMove}</p>
      </div>
    </section>
  );
}

const styles: Record<string, React.CSSProperties> = {
  card: {
    border: "1px solid #dbeafe",
    background: "linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)",
    borderRadius: 16,
    padding: 20,
    marginBottom: 20,
    boxShadow: "0 10px 30px rgba(15, 23, 42, 0.06)"
  },
  header: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 16,
    marginBottom: 10
  },
  eyebrow: {
    margin: 0,
    color: "#2563eb",
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase"
  },
  title: {
    margin: "4px 0 0",
    color: "#0f172a",
    fontSize: 22,
    lineHeight: 1.2
  },
  summary: {
    margin: "12px 0 18px",
    color: "#0f172a",
    fontSize: 18,
    fontWeight: 800,
    lineHeight: 1.55,
    maxWidth: 1100
  },
  freshnessNotice: {
    margin: "-6px 0 14px",
    padding: "9px 11px",
    border: "1px solid #fde68a",
    borderRadius: 9,
    color: "#92400e",
    background: "#fffbeb",
    fontSize: 13,
    lineHeight: 1.45,
  },
  insightGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 12
  },
  insightCard: {
    border: "1px solid #e2e8f0",
    background: "#ffffff",
    borderRadius: 12,
    padding: 14
  },
  insightLabel: {
    margin: "0 0 6px",
    color: "#64748b",
    fontSize: 12,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.06em"
  },
  insightText: {
    margin: 0,
    color: "#0f172a",
    fontSize: 14,
    lineHeight: 1.5
  },
  nextMove: {
    marginTop: 14,
    border: "1px solid #bfdbfe",
    background: "#eff6ff",
    borderRadius: 12,
    padding: 14
  },
  nextMoveLabel: {
    margin: "0 0 5px",
    color: "#1d4ed8",
    fontSize: 12,
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: "0.06em"
  },
  nextMoveText: {
    margin: 0,
    color: "#1e293b",
    fontSize: 14,
    lineHeight: 1.5
  }
};
